import * as React from 'react';
import { Input } from '@/components/ui/Input.jsx';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { ChevronLeft, ChevronRight, Download, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn.js';

/**
 * Generic, controlled-ish DataTable with client-side search/sort/pagination.
 *
 * columns: [{ key, header, render?, sortable?, className?, accessor?, exportValue? }]
 */
export function DataTable({
  columns,
  data = [],
  loading = false,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No records found',
  pageSize = 10,
  enableSearch = true,
  enableExport = true,
  exportFilename = 'export.csv',
  onRowClick,
  rowKey = (r, i) => r.id ?? i,
  rowClassName,
  toolbar,
  pagination,
  onExport,
}) {
  const [q, setQ] = React.useState('');
  const [sort, setSort] = React.useState({ key: null, dir: 'asc' });
  const [page, setPage] = React.useState(1);
  const [exporting, setExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState('');

  const filtered = React.useMemo(() => {
    if (pagination || !q) return data;
    const ql = q.toLowerCase();
    return data.filter((row) =>
      columns.some((c) => {
        const v = typeof c.accessor === 'function' ? c.accessor(row) : c.accessor ? row[c.accessor] : row[c.key];
        return v != null && String(v).toLowerCase().includes(ql);
      }),
    );
  }, [data, q, columns, pagination]);

  const sorted = React.useMemo(() => {
    if (pagination || !sort.key) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const get = (r) => (typeof col.accessor === 'function' ? col.accessor(r) : col.accessor ? r[col.accessor] : r[col.key]);
    return [...filtered].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [filtered, sort, columns, pagination]);

  const totalRecords = pagination ? pagination.total : sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(pagination ? pagination.page : page, totalPages);
  const paged = pagination ? data : sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
  const activeSort = pagination ? pagination.sort : sort;
  const changePage = (value) => pagination ? pagination.onChange({ ...pagination, page: value }) : setPage(value);

  React.useEffect(() => {
    setPage(1);
  }, [q, data]);

  const toggleSort = (key) => {
    if (pagination) {
      const next = activeSort.key !== key ? { key, dir: 'asc' } : activeSort.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' };
      pagination.onChange({ ...pagination, page: 1, sort: next });
      return;
    }
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };

  const exportCsv = async () => {
    setExporting(true);
    setExportError('');
    try {
      const exportRows = onExport ? await onExport() : sorted;
      const escape = (v) => {
        if (v == null) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      };
      const header = columns.map((c) => escape(c.header)).join(',');
      const rows = exportRows.map((row) =>
        columns
          .map((c) => {
            if (c.exportValue) return escape(c.exportValue(row));
            if (typeof c.accessor === 'function') return escape(c.accessor(row));
            if (c.accessor) return escape(row[c.accessor]);
            return escape(row[c.key]);
          })
          .join(','),
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFilename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {enableSearch && (
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder={searchPlaceholder}
              value={pagination ? pagination.search : q}
              onChange={(e) => pagination ? pagination.onChange({ ...pagination, page: 1, search: e.target.value }) : setQ(e.target.value)}
              className="pl-8"
            />
          </div>
        )}
        <div className="flex-1" />
        {toolbar}
        {enableExport && (
          <Button variant="outline" size="sm" disabled={exporting} onClick={exportCsv}>
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        )}
      </div>

      {exportError && <p role="alert" className="text-sm text-red-600">{exportError}</p>}
      <Table>
        <THead>
          <TR>
            {columns.map((c) => (
              <TH key={c.key} className={cn(c.headClassName)}>
                {c.sortable ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-200"
                  >
                    {c.header}
                    {activeSort.key === c.key &&
                      (activeSort.dir === 'asc' ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      ))}
                  </button>
                ) : (
                  c.header
                )}
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {loading ? (
            <TR>
              <TD colSpan={columns.length} className="text-center text-slate-500 py-6">
                Loading...
              </TD>
            </TR>
          ) : paged.length === 0 ? (
            <TR>
              <TD colSpan={columns.length} className="text-center text-slate-500 py-6">
                {emptyMessage}
              </TD>
            </TR>
          ) : (
            paged.map((row, i) => (
              <TR
                key={rowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(onRowClick && 'cursor-pointer', rowClassName?.(row, i))}
              >
                {columns.map((c) => (
                  <TD key={c.key} className={c.className}>
                    {c.render
                      ? c.render(
                        typeof c.accessor === 'function' ? c.accessor(row) : c.accessor ? row[c.accessor] : row[c.key],
                        row,
                      )
                      : typeof c.accessor === 'function'
                        ? c.accessor(row)
                        : c.accessor
                          ? row[c.accessor]
                          : row[c.key]}
                  </TD>
                ))}
              </TR>
            ))
          )}
        </TBody>
      </Table>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <div>
          {totalRecords === 0
            ? '0 records'
            : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, totalRecords)} of ${totalRecords}`}
        </div>
        <div className="flex items-center gap-2">
          <Button aria-label="Previous page" variant="outline" size="sm" onClick={() => changePage(Math.max(1, safePage - 1))} disabled={loading || safePage <= 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>
            Page {safePage} of {totalPages}
          </span>
          <Button aria-label="Next page" variant="outline" size="sm" onClick={() => changePage(Math.min(totalPages, safePage + 1))} disabled={loading || safePage >= totalPages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
